import { Link } from 'react-router-dom';
import { useIntegrations, useSyncNow } from '../lib/api';
import { relativeStamp } from '../lib/format';
import { Chip, ErrorBox, PageHeader, Skeleton } from '../components/ui';
import { useToast } from '../components/Toast';
import './integrations.css';

const READS: { title: string; help: string; mode: 'Read' | 'Two-way' | 'Write'; on: boolean; note?: string }[] = [
  { title: 'Call recordings', help: 'Every inbound and outbound call', mode: 'Read', on: true },
  { title: 'Texts, emails & forms', help: 'Every SMS, email and form fill on the contact', mode: 'Read', on: true },
  { title: 'Contacts', help: 'Name, phone, lead source', mode: 'Read', on: true },
  { title: 'Opportunity value', help: 'Used for pipeline at risk and recovered revenue', mode: 'Read', on: true },
  { title: 'Notes', help: 'Added in either app, shown in both', mode: 'Two-way', on: true },
  { title: 'Tag rescue leads', help: 'Adds a tag in HighLevel when a lead is flagged', mode: 'Write', on: false, note: 'Coming soon' },
  { title: 'Create follow-up task', help: 'Creates a HighLevel task for the assigned rep', mode: 'Write', on: false, note: 'Coming soon' },
];

const ALERTS = [
  { name: 'Slack', help: 'Rescue alerts and the daily summary in a channel.' },
  { name: 'Email', help: 'Daily summary and weekly coaching report.' },
  { name: 'SMS', help: 'High-value rescue alerts to a manager’s phone.' },
];

/** Handoff screen 11. */
export function Integrations() {
  const { data, error, isLoading } = useIntegrations();
  const sync = useSyncNow();
  const toast = useToast();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const n = (x: number) => x.toLocaleString('en-US');

  return (
    <div className="page">
      <PageHeader title="Integrations" subtitle="Coach Lex reads calls, texts, emails, contacts and pipelines from HighLevel. It never sends messages or places calls." />
      <span className="section-label">Conversations & CRM</span>
      {error ? (
        <ErrorBox error={error} />
      ) : isLoading || !data ? (
        <Skeleton height={420} />
      ) : (
        <section className="card stack-16">
          <div className="crm-head">
            <span className="crm-logo">HL</span>
            <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="card-title" style={{ fontSize: 16 }}>
                  HighLevel
                </span>
                {data.status === 'token_invalid' ? (
                  <Chip tone="red">Token invalid</Chip>
                ) : data.importPct != null ? (
                  <Chip tone="amber">Importing · {data.importPct}%</Chip>
                ) : (
                  <Chip tone="green">Connected</Chip>
                )}
              </span>
              <span className="card-meta">
                Sub-account: {data.subAccount} · Location {data.locationMasked} · {n(data.synced.calls)} calls, {n(data.synced.texts)} texts and {n(data.synced.emails)} emails synced
                {data.lastSyncAt ? ` · Last sync ${relativeStamp(data.lastSyncAt, tz).toLowerCase()}` : ''}
              </span>
            </span>
            <button
              className="btn btn--secondary"
              disabled={sync.isPending || data.status === 'token_invalid'}
              onClick={() => sync.mutate(undefined, { onSuccess: () => toast({ text: 'Sync started. New activity shows up within a minute or two.' }) })}
            >
              {sync.isPending ? 'Starting…' : 'Sync now'}
            </button>
          </div>
          {data.status === 'token_invalid' && <div className="hint-box">HighLevel rejected the token. An app owner can fix the connection in Admin → Companies.</div>}
          {sync.error && <ErrorBox error={sync.error} />}
          <div className="crm-cols">
            <div>
              <span className="card-title" style={{ fontSize: 14 }}>
                What Coach Lex syncs
              </span>
              {READS.map((r) => (
                <div key={r.title} className="sync-row" style={{ opacity: r.on ? 1 : 0.6 }}>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 13.5 }}>{r.title}</span>
                    <span className="card-meta">
                      {r.help}
                      {r.title.startsWith('Texts') && !data.formsAvailable ? '. Form fills need the forms.readonly scope on the token.' : ''}
                    </span>
                  </span>
                  <span className="sync-row__right">
                    <span className="chan-chip">{r.mode}</span>
                    {r.on ? <Chip tone="green">On</Chip> : <span className="card-meta">{r.note}</span>}
                  </span>
                </div>
              ))}
            </div>
            <div className="stack-12">
              <span className="card-title" style={{ fontSize: 14 }}>
                Journey milestones
              </span>
              <p className="prose" style={{ margin: 0, fontSize: 13 }}>
                Coach Lex doesn’t use pipeline stages, because opportunities move back and forth. Milestones come from HighLevel <b>tags</b> and <b>appointments marked showed</b>, and count the first time they happen.
              </p>
              <div className="crm-counts">
                <span>Tags found</span>
                <b>{n(data.tags)}</b>
                <span>Calendars found</span>
                <b>{n(data.calendars)}</b>
                <span>Milestones set up</span>
                <b>{n(data.milestones)}</b>
              </div>
              <Link to="/settings/journey" style={{ fontSize: 13, fontWeight: 500 }}>
                Edit milestones in Journey setup →
              </Link>
            </div>
          </div>
        </section>
      )}

      <span className="section-label">Alerts</span>
      <div className="alert-cards">
        {ALERTS.map((a) => (
          <section key={a.name} className="card stack-10">
            <div className="card-head" style={{ alignItems: 'center' }}>
              <span className="card-title">{a.name}</span>
              <Chip tone="grey">Not connected</Chip>
            </div>
            <span className="card-meta">{a.help}</span>
            <span className="card-meta">Coming soon. Choose which alerts you want in Settings → Team & workspace → Notifications.</span>
          </section>
        ))}
      </div>
    </div>
  );
}
